// File: frontend/src/pages/CalendarPage.js
// Version: 5.0 (quick-260915-kal — экран вернулся на общий слой, зашитых цветов 0)
// Date: 2026-09-15
// Description:
// - 100% Real Google Calendar synchronization with NO mock/fake events.
// - Shows real appointments fetched via apiService.getCalendarEvents.
// - Type filtering: ALL, CONSULTATION, HEARING, INTERNAL, CASE_REVIEW based on event summary/description.
// - Date navigation: previous/next week and today.
// - Interactive: click empty slot or "+ Встреча" to create appointment in Google Calendar.
// - Окно деталей консультации (клиент, телефон, почта, ссылка на Google Meet).
// - Двухколоночная раскладка: фильтры + подсказка Katifi + состояние обмена слева,
//   недельная сетка справа.
//
// ═══════════════════════════════════════════════════════════════════════════
// ЧТО ИЗМЕНЕНО 15.09.2026 И ПОЧЕМУ (quick-260915-kal)
// ───────────────────────────────────────────────────────────────────────────
// Слияние принесло эту версию экрана с боевого сервера. Функции у неё
// НАСТОЯЩИЕ и сохранены все до одной. Оформление было сделано тем же
// способом, что и «Этап 1 унификации»: 19 зашитых цветов в этом файле и 59 в
// собственном CSS, а общего слоя экран не касался вовсе. Прибор
// `scripts/ohvat-sloya.py` из-за этого стал КРАСНЫМ тремя строками сразу:
// экран СЛЕТЕЛ СО СЛОЯ (11 → 10), зашитых цветов стало 1212 при потолке 1033,
// смысловых классов 207 при поле 241.
//
// 🔴 ЦВЕТ УШЁЛ ИЗ ЭТОГО ФАЙЛА ЦЕЛИКОМ. Объект `TYPE_STYLES` хранил четыре
//    ПАРЫ зашитых hex и раздавал их инлайновым `style={{…}}`. Инлайновый стиль
//    — это ВТОРОЙ держатель оформления, и спорить с ним каскадом нечем: он
//    бьёт любой селектор. Теперь тип встречи несёт ИМЯ КЛАССА
//    (`cal-ev-hearing`, `cal-dot-hearing`), а цвет объявлен ровно один раз — в
//    `CalendarPage.css`, через имена экрана `--cal-*`.
//
// 🔴 ЭКРАН ВЕРНУЛСЯ НА СЛОЙ ЧЕРЕЗ `components/FitenkoUzor`. Взята ВТОРАЯ форма
//    шапки (`ShapkaBezDeystviya`, БЕЗ главного действия) — тот же выбор и та же
//    причина, что стояли здесь до слияния: главных действий у экрана ДВА
//    равноправных («Часы приема» и «+ Встреча»), обе кнопки остаются в своей
//    панели, а поднять одну из них в правый угол шапки — решение владельца, а
//    не побочный эффект уборки.
//
// 🔴 ШАПКА СЛОЯ РИСУЕТСЯ ТОЛЬКО У fitenko, И ЭТО НЕ ВКУС. Её оформление
//    (`.fk-page-head`, `.fk-page-kicker`, `.fk-page-h1` …) объявлено в
//    `styles/fitenko-shell.css` §7.6 ПОД РУБЕЖОМ `html.fitenko`. У mlg, eln и
//    elfny этих правил нет, и безусловная шапка дала бы им неоформленную
//    разметку. Маршрут /calendar общий на четырёх арендаторов.
//
// 🔴 ТРЕТИЙ ИСХОД ЗАПРОСА ВЕРНУЛСЯ — И ЭТО ПОЧИНКА, А НЕ УБОРКА. Состояние
//    `error` в пришедшей версии ЗАПОЛНЯЛОСЬ, но НИГДЕ НЕ ОТРИСОВЫВАЛОСЬ:
//    при отказе ручки экран показывал пустую сетку, а карточка Katifi
//    утверждала «На этой неделе… пока нет запланированных встреч». Отказ
//    измерителя выдавался за «встреч нет» — ровно та порча, из-за которой
//    12.09 потерялись 89 событий, и ровно то, что запрещает
//    [[feedback_unknown_is_not_no]]. Исходов снова ТРИ и они РАЗЛИЧИМЫ:
//    «спрашиваем», «не смогли спросить» (с НАЗВАННЫМ окном и причиной),
//    «за это окно записей нет».
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    ChevronLeft,
    ChevronRight,
    Plus,
    Sparkles,
    RotateCcw,
    Settings,
    AlertCircle
} from 'lucide-react';
import {
    format,
    addDays,
    subDays,
    startOfWeek,
    endOfWeek,
    addWeeks,
    subWeeks,
    isSameDay,
    parseISO
} from 'date-fns';
import { ru } from 'date-fns/locale';

import apiService from '../services/apiService';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { ShapkaBezDeystviya, Sostoyanie } from '../components/FitenkoUzor';
import { LEGACY_DESTINATIONS } from '../config/fitenkoNav';
import NewAppointmentModal from '../components/Calendar/NewAppointmentModal';
import WorkingHoursSettings from '../components/Calendar/WorkingHoursSettings';
import AppointmentDetailsPopup from '../components/Calendar/AppointmentDetailsPopup';
import './CalendarPage.css';

const HOURS = [
    "08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00"
];

// Тип встречи: ПОДПИСЬ и ИМЯ КЛАССА. Цвета здесь нет и быть не должно —
// держатель цвета один, и он в CalendarPage.css (`--cal-consultation` и
// соседи). До правки тут лежали четыре пары зашитых hex.
const TYPE_META = {
    CONSULTATION: { label: 'Консультации',   klass: 'consultation' },
    HEARING:      { label: 'USCIS Слушания', klass: 'hearing' },
    INTERNAL:     { label: 'AI Консилиум',   klass: 'internal' },
    CASE_REVIEW:  { label: 'Анализ дела',    klass: 'case-review' }
};

// Адрес по ключу у держателя адресов (config/fitenkoNav.js, LEGACY_DESTINATIONS).
// Приём взят дословно у соседних экранов слоя; строкой адрес НЕ пишется —
// второй держатель разошёлся бы с первым молча.
const adresPoKlyuchu = (kluch) => {
    const d = LEGACY_DESTINATIONS.find((z) => z.key === kluch);
    return d ? d.path : null;
};

// Подпись границы окна для человека. Формат дд.мм.гггг — тот же, что в приёмке.
const podpisDaty = (d) => (d instanceof Date && !isNaN(d) ? format(d, 'dd.MM.yyyy') : '—');

export default function CalendarPage() {
    const { user } = useAuth();
    // 🔴 РУБЕЖ АРЕНДАТОРА СТОИТ ВМЕСТЕ С ОСТАЛЬНЫМИ ХУКАМИ, а не перед ранним
    //    возвратом: порядок хуков React обязан быть ОДИН на каждой отрисовке,
    //    а признак арендатора приходит асинхронно (BrandingContext).
    const { isFitenko } = useBranding();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [calendarView, setCalendarView] = useState('week'); // 'day' | 'week' | 'month' | 'agenda'
    const [selectedTypeFilter, setSelectedTypeFilter] = useState('ALL');
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // Окно, ПРО КОТОРОЕ у нас есть ответ. Это не второй держатель показанного
    // окна: пока ответ не пришёл, спрошенное и отвеченное — разные вещи, и
    // третий исход обязан называть то окно, про которое ручка уже сказала
    // «пусто».
    const [oknoOtveta, setOknoOtveta] = useState(null);

    // Modals
    const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
    const [isWorkingHoursModalOpen, setIsWorkingHoursModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isDetailsPopupOpen, setIsDetailsPopupOpen] = useState(false);
    const [preselectedSlotDate, setPreselectedSlotDate] = useState(null);

    // Monday to Sunday of the active week
    const weekStart = useMemo(() => startOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);
    const weekEnd = useMemo(() => endOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);

    // 7 days of the week
    const weekDays = useMemo(() => {
        return Array.from({ length: 7 }).map((_, i) => {
            const d = addDays(weekStart, i);
            const dayNamesShort = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
            return {
                dayIndex: i,
                dateObj: d,
                dayName: `${format(d, 'dd')} ${dayNamesShort[i]}`,
                formattedDate: format(d, 'd MMMM', { locale: ru }),
                isToday: isSameDay(d, new Date())
            };
        });
    }, [weekStart]);

    // Range header title: e.g. "14 – 20 сентября 2026"
    const rangeHeaderTitle = useMemo(() => {
        const startDay = format(weekStart, 'd');
        const endStr = format(weekEnd, 'd MMMM yyyy', { locale: ru });
        return `${startDay} – ${endStr}`;
    }, [weekStart, weekEnd]);

    // Real API Event Fetcher (NO FAKE / MOCK EVENTS)
    const fetchEvents = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Include full range with margins
            const s = new Date(weekStart);
            s.setHours(0, 0, 0, 0);
            const e = new Date(weekEnd);
            e.setHours(23, 59, 59, 999);

            const startISO = s.toISOString();
            const endISO = e.toISOString();

            const res = await apiService.getCalendarEvents(startISO, endISO);

            let rawList = [];
            if (Array.isArray(res)) rawList = res;
            else if (res && Array.isArray(res.events)) rawList = res.events;
            else if (res && Array.isArray(res.items)) rawList = res.items;
            else {
                // 🔴 ЧЕТВЁРТАЯ ФОРМА ОТВЕТА НАЗЫВАЕТСЯ ВСЛУХ. Три известные
                //    формы разобраны выше; всё остальное молча становилось
                //    пустым списком, и человек видел «за это окно записей
                //    нет». Это ровно та подмена, из-за которой 12.09
                //    потерялись 89 событий: отказ измерителя выдавался за
                //    «нет». Поведение не меняется (список пуст), но пустота
                //    перестаёт быть НЕМОЙ.
                console.warn('Незнакомая форма ответа getCalendarEvents — событий не извлечено', res);
            }

            const parsed = (rawList || []).map(ev => {
                const startRaw = ev.start?.dateTime || ev.start?.date || ev.start;
                const endRaw = ev.end?.dateTime || ev.end?.date || ev.end;
                const startDate = typeof startRaw === 'string' ? parseISO(startRaw) : new Date(startRaw);
                const endDate = typeof endRaw === 'string' ? parseISO(endRaw) : new Date(endRaw);

                const summary = ev.summary || ev.title || '(Без названия)';
                const sumLower = summary.toLowerCase();

                let evType = 'CONSULTATION';
                if (sumLower.includes('слушание') || sumLower.includes('hearing') || sumLower.includes('uscis') || sumLower.includes('court')) {
                    evType = 'HEARING';
                } else if (sumLower.includes('консилиум') || sumLower.includes('council') || sumLower.includes('internal')) {
                    evType = 'INTERNAL';
                } else if (sumLower.includes('анализ') || sumLower.includes('case') || sumLower.includes('review') || sumLower.includes('разбор')) {
                    evType = 'CASE_REVIEW';
                } else {
                    evType = 'CONSULTATION';
                }

                // Calculate dayIndex (0 = Monday, ..., 6 = Sunday)
                let dayIdx = (startDate.getDay() + 6) % 7;

                // Client name extraction if available
                let client = ev.client_name || ev.clientName || '';
                if (!client && ev.lead_details?.full_name) {
                    client = ev.lead_details.full_name;
                }
                if (!client && summary.includes(':')) {
                    const parts = summary.split(':');
                    if (parts.length > 1) {
                        client = parts[1].trim();
                    }
                }

                return {
                    id: ev.id || `ev-${Math.random()}`,
                    title: summary,
                    clientName: client,
                    start: startDate,
                    end: endDate,
                    startTime: format(startDate, 'HH:mm'),
                    endTime: format(endDate, 'HH:mm'),
                    dayIndex: dayIdx,
                    type: evType,
                    raw: ev
                };
            });

            setEvents(parsed);
            // Ответ ПРО ЭТО окно получен — и только теперь «пусто» значит «пусто».
            setOknoOtveta({ nachalo: s, konec: e });
        } catch (err) {
            console.error('Failed to load Google Calendar events:', err);
            setError(err.message || 'Ошибка загрузки Google Calendar');
            setEvents([]);
            // 🔴 Окно ответа СБРАСЫВАЕТСЯ: ответа про него нет, и выдавать
            //    отказ за «записей нет» нельзя.
            setOknoOtveta(null);
        } finally {
            setLoading(false);
        }
    }, [weekStart, weekEnd]);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    // Navigation handlers
    const handlePrev = () => {
        if (calendarView === 'day') setCurrentDate(prev => subDays(prev, 1));
        else setCurrentDate(prev => subWeeks(prev, 1));
    };

    const handleNext = () => {
        if (calendarView === 'day') setCurrentDate(prev => addDays(prev, 1));
        else setCurrentDate(prev => addWeeks(prev, 1));
    };

    const handleToday = () => {
        setCurrentDate(new Date());
    };

    // Filter events by selected type
    const filteredEvents = useMemo(() => {
        if (selectedTypeFilter === 'ALL') return events;
        return events.filter(e => e.type === selectedTypeFilter);
    }, [events, selectedTypeFilter]);

    // Count badges for meeting filters
    const filterCounts = useMemo(() => {
        return {
            ALL: events.length,
            CONSULTATION: events.filter(e => e.type === 'CONSULTATION').length,
            HEARING: events.filter(e => e.type === 'HEARING').length,
            INTERNAL: events.filter(e => e.type === 'INTERNAL').length,
            CASE_REVIEW: events.filter(e => e.type === 'CASE_REVIEW').length
        };
    }, [events]);

    const handleSlotClick = (dateObj, hourStr) => {
        const [h, m] = hourStr.split(':').map(Number);
        const slotDate = new Date(dateObj);
        slotDate.setHours(h, m, 0, 0);
        setPreselectedSlotDate(slotDate);
        setIsAppointmentModalOpen(true);
    };

    const handleEventClick = (e, ev) => {
        e.stopPropagation();
        setSelectedEvent(ev.raw || ev);
        setIsDetailsPopupOpen(true);
    };

    const isConnected = user?.google_calendar_integrated !== false;

    // 🔴 ТРИ РАЗЛИЧИМЫХ ИСХОДА, И «ПУСТО» — ТОЛЬКО ТРЕТИЙ ИЗ НИХ.
    const pustoZaOknom = !loading && !error && !!oknoOtveta && events.length === 0;

    return (
        <div className="cal-root-container text-foreground">
            {/* 🔴 ВТОРАЯ ФОРМА ШАПКИ (БЕЗ главного действия) и только у fitenko:
                   её оформление объявлено под рубежом арендатора. */}
            {isFitenko && (
                <ShapkaBezDeystviya
                    nadzagolovok="CALENDAR"
                    zagolovok="Календарь"
                    podstroka="Встречи за то окно, которое вы сейчас видите."
                    nazadKuda={adresPoKlyuchu('dashboard')}
                    nazadPodpis="← Назад к работе"
                />
            )}

            {/* 1. TOP HEADER TOOLBAR */}
            <div className="cal-header-bar bg-card border-border">
                <div className="cal-nav-wrapper">
                    <div className="cal-arrows-group bg-background border-border">
                        <button
                            type="button"
                            className="cal-arrow-btn text-foreground"
                            onClick={handlePrev}
                            title="Предыдущая неделя"
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <button
                            type="button"
                            className="cal-today-btn text-foreground border-border"
                            onClick={handleToday}
                        >
                            Сегодня
                        </button>
                        <button
                            type="button"
                            className="cal-arrow-btn text-foreground"
                            onClick={handleNext}
                            title="Следующая неделя"
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>

                    <div className="cal-heading-wrapper">
                        <h2 className="cal-heading-title">{rangeHeaderTitle}</h2>
                        <p className="cal-sub-label">
                            Coral Springs / Miami Office • America/New_York (EST)
                        </p>
                    </div>
                </div>

                <div className="cal-controls-right">
                    {/* View Switcher Pills */}
                    <div className="cal-view-switcher bg-background border-border">
                        <button
                            type="button"
                            className={`cal-view-tab ${calendarView === 'day' ? 'active' : ''}`}
                            onClick={() => setCalendarView('day')}
                        >
                            День
                        </button>
                        <button
                            type="button"
                            className={`cal-view-tab ${calendarView === 'week' ? 'active' : ''}`}
                            onClick={() => setCalendarView('week')}
                        >
                            Неделя
                        </button>
                        <button
                            type="button"
                            className={`cal-view-tab ${calendarView === 'month' ? 'active' : ''}`}
                            onClick={() => setCalendarView('month')}
                        >
                            Месяц
                        </button>
                        <button
                            type="button"
                            className={`cal-view-tab ${calendarView === 'agenda' ? 'active' : ''}`}
                            onClick={() => setCalendarView('agenda')}
                        >
                            Повестка
                        </button>
                    </div>

                    {/* Working Hours Button */}
                    <button
                        type="button"
                        className="cal-action-btn-secondary bg-card border-border text-foreground"
                        onClick={() => setIsWorkingHoursModalOpen(true)}
                        title="Настроить часы приема"
                    >
                        <Settings size={14} />
                        <span>Часы приема</span>
                    </button>

                    {/* Primary Gold Create Appointment Button */}
                    <button
                        type="button"
                        className="cal-btn-create-appointment bg-primary text-foreground"
                        onClick={() => {
                            setPreselectedSlotDate(null);
                            setIsAppointmentModalOpen(true);
                        }}
                    >
                        <Plus size={16} />
                        <span>+ Встреча</span>
                    </button>
                </div>
            </div>

            {/* ТРИ ИСХОДА ЗАПРОСА. Сетка при этом ОСТАЁТСЯ на экране всегда:
                подменять её сообщением значило бы отнять у человека стрелки,
                которыми он перелистнёт на непустое окно. */}
            {loading && (
                <div className="cal-state bg-card border-border text-foreground" role="status">
                    <RotateCcw size={14} className="animate-spin" />
                    <Sostoyanie tekst={`Спрашиваем Google Calendar за окно ${podpisDaty(weekStart)} — ${podpisDaty(weekEnd)}…`} />
                </div>
            )}
            {!loading && error && (
                <div className="cal-state cal-state--otkaz bg-card text-foreground" role="alert">
                    <AlertCircle size={14} />
                    <Sostoyanie tekst={`Не смогли спросить Google Calendar за окно ${podpisDaty(weekStart)} — ${podpisDaty(weekEnd)}: ${error}. Это НЕ значит, что встреч нет.`} />
                    <button type="button" className="cal-state-retry" onClick={fetchEvents}>
                        Повторить
                    </button>
                </div>
            )}
            {pustoZaOknom && (
                <div className="cal-state bg-card border-border text-foreground" role="status">
                    <Sostoyanie tekst={`За окно ${podpisDaty(oknoOtveta.nachalo)} — ${podpisDaty(oknoOtveta.konec)} записей нет`} />
                </div>
            )}

            {/* 2. TWO COLUMN WORKSPACE */}
            <div className="cal-workspace-grid">

                {/* LEFT SIDEBAR (1/4) */}
                <div className="cal-sidebar-column">

                    {/* Meeting Types Filter Card */}
                    <div className="cal-card-box bg-card border-border">
                        <div className="cal-card-header-title">Фильтр типов встреч</div>
                        <div className="cal-filter-items-group">
                            <button
                                type="button"
                                onClick={() => setSelectedTypeFilter('ALL')}
                                className={`cal-filter-item-row ${selectedTypeFilter === 'ALL' ? 'active' : ''}`}
                            >
                                <span>Все события</span>
                                <span className="cal-count-badge">{filterCounts.ALL}</span>
                            </button>

                            {Object.keys(TYPE_META).map((tip) => (
                                <button
                                    key={tip}
                                    type="button"
                                    onClick={() => setSelectedTypeFilter(tip)}
                                    className={`cal-filter-item-row ${selectedTypeFilter === tip ? 'active' : ''}`}
                                >
                                    <div className="cal-filter-item-left">
                                        <span className={`cal-dot cal-dot-${TYPE_META[tip].klass}`}></span>
                                        <span>{TYPE_META[tip].label}</span>
                                    </div>
                                    <span className="cal-count-badge">{filterCounts[tip]}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Katifi Smart Recommendation Card */}
                    <div className="cal-katifi-card-box bg-accent border-primary/30">
                        <div className="cal-katifi-badge-head">
                            <Sparkles size={14} />
                            <span>Умный график Katifi</span>
                        </div>
                        {/* 🔴 ТРИ ОТВЕТА, А НЕ ДВА. Прежде отказ ручки и пустое
                               окно говорили ОДНИМИ СЛОВАМИ «встреч нет» — то
                               есть неизвестное выдавалось за «нет». */}
                        <p className="cal-katifi-body-text text-foreground">
                            {error
                                ? "Сколько встреч на этой неделе — НЕ ЗНАЕМ: Google Calendar не ответил. Обновите обмен, прежде чем обещать клиенту свободное окно."
                                : (loading
                                    ? "Спрашиваем Google Calendar о встречах этой недели…"
                                    : (events.length === 0
                                        ? "На этой неделе в Google Calendar пока нет запланированных встреч. Все слоты свободны для записи клиентов."
                                        : `На этой неделе запланировано ${events.length} встреч. Katifi готова принять новых клиентов в свободные окна.`))}
                        </p>
                        <button
                            type="button"
                            className="cal-katifi-action-btn"
                            onClick={() => {
                                setPreselectedSlotDate(null);
                                setIsAppointmentModalOpen(true);
                            }}
                        >
                            Назначить встречу →
                        </button>
                    </div>

                    {/* Google Sync Status */}
                    <div className="cal-sync-card-box bg-card border-border">
                        <div className="cal-sync-status-row text-foreground">
                            <span>Google Calendar</span>
                            <span
                                className={`cal-sync-live-dot ${isConnected && !error ? 'is-live' : 'is-off'}`}
                                title={error
                                    ? "Не смогли спросить Google Calendar"
                                    : (isConnected ? "Синхронизация активна" : "Требуется подключение")}
                            ></span>
                        </div>
                        <p className="cal-sync-note">
                            {loading
                                ? 'Идет синхронизация...'
                                : (error
                                    ? 'Не смогли спросить'
                                    : (isConnected ? 'Синхронизировано' : 'Не подключен'))}
                        </p>
                        <button
                            type="button"
                            className="cal-refresh-sync-btn bg-background border-border"
                            onClick={fetchEvents}
                            disabled={loading}
                        >
                            <RotateCcw size={12} className={loading ? 'animate-spin' : ''} />
                            <span>{loading ? 'Обновление...' : 'Обновить из Google'}</span>
                        </button>
                    </div>

                </div>

                {/* RIGHT SCHEDULE GRID (3/4) */}
                <div className="cal-schedule-main-card bg-card border-border">
                    <div className="cal-grid-min-wrapper">

                        {/* 7-Days Header Row */}
                        <div className="cal-days-header-row bg-background border-border">
                            {weekDays.map(day => (
                                <div
                                    key={day.dayIndex}
                                    className={`cal-day-header-cell border-border ${day.isToday ? 'is-today' : ''}`}
                                >
                                    <div className="cal-day-title-sub">{day.dayName}</div>
                                    <div className="cal-day-num-title text-foreground">{day.formattedDate.split(' ')[0]}</div>
                                </div>
                            ))}
                        </div>

                        {/* Hours Slot Rows (08:00 - 18:00) */}
                        <div className="cal-slots-body">
                            {HOURS.map((hourStr, hIdx) => {
                                const hourPrefix = hourStr.split(':')[0];

                                return (
                                    <div key={hIdx} className="cal-time-slot-row border-border">
                                        {weekDays.map((day, dIdx) => {
                                            const slotEvents = filteredEvents.filter(ev => {
                                                const matchesDay = ev.dayIndex === dIdx;
                                                const matchesHour = ev.startTime && ev.startTime.startsWith(hourPrefix);
                                                return matchesDay && matchesHour;
                                            });

                                            return (
                                                <div
                                                    key={dIdx}
                                                    className={`cal-grid-slot-cell border-border ${day.isToday ? 'is-today-cell' : ''}`}
                                                    onClick={() => handleSlotClick(day.dateObj, hourStr)}
                                                >
                                                    <span className="cal-slot-time-caption">{hourStr}</span>

                                                    {slotEvents.map(ev => {
                                                        const meta = TYPE_META[ev.type] || TYPE_META.CONSULTATION;
                                                        return (
                                                            <div
                                                                key={ev.id}
                                                                onClick={(e) => handleEventClick(e, ev)}
                                                                className={`cal-slot-event-card cal-ev-${meta.klass}`}
                                                                title={`${ev.title} (${ev.startTime} - ${ev.endTime})`}
                                                            >
                                                                <div className="cal-event-card-header">
                                                                    {ev.startTime} {ev.title}
                                                                </div>
                                                                {ev.clientName && (
                                                                    <div className="cal-event-card-client">
                                                                        {ev.clientName}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>

                    </div>
                </div>

            </div>

            {/* Existing Functionality Modals */}
            <NewAppointmentModal
                isOpen={isAppointmentModalOpen}
                onClose={() => setIsAppointmentModalOpen(false)}
                onAppointmentCreated={fetchEvents}
                initialDate={preselectedSlotDate}
            />

            <WorkingHoursSettings
                isOpen={isWorkingHoursModalOpen}
                onClose={() => setIsWorkingHoursModalOpen(false)}
                onSave={() => {
                    setIsWorkingHoursModalOpen(false);
                    fetchEvents();
                }}
            />

            <AppointmentDetailsPopup
                isOpen={isDetailsPopupOpen && !!selectedEvent}
                onClose={() => {
                    setIsDetailsPopupOpen(false);
                    setSelectedEvent(null);
                }}
                event={selectedEvent}
                eventData={selectedEvent}
                onEventUpdated={fetchEvents}
            />
        </div>
    );
}
